import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { StepComponent } from '../../step.component';
import { PlanStep } from '../../../services/PlanStep';
import { ActivityComponent } from './activity/activity.component';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { AppLoadingService } from '../../../../app-loading-panel/app-loading.service';
import { ActivityService, StrtActivityDates } from '../../../services/activity.service';
import { PriorityService } from '../../../services/priority.service';
import { Observable, Subscription } from 'rxjs/index';
import { SelectItem } from 'primeng/components/common/selectitem';
import { DataUtils } from '../../../../shared/utilities/data-utils';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import { StrtObjective } from '../../../../shared/models/plan/StrtObjective';
import { StrtInitiative } from '../../../../shared/models/plan/StrtInitiative';
import { StrtActivity } from '../../../../shared/models/plan/StrtActivity';
import { PriorityDropdownComponent } from '../../../../shared/components/priority-dropdown/priority-dropdown.component';
import { IPriorityDropdownOptionValue, PriorityDropdownOption } from '../../../../shared/models/IPriorityDropdownOption';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { ResourceService } from '../../../services/resource.service';

@Component({
  selector: 'app-design-implementation-plan',
  templateUrl: './design-implementation-plan.component.html',
  styleUrls: ['./design-implementation-plan.component.scss']
})
export class DesignImplementationPlanComponent extends StepComponent<StrtActivityDates> implements OnInit, OnDestroy {

  @ViewChild(ActivityComponent) private activityComp: ActivityComponent;
  @ViewChild('activitySidebar') activitySidebarElementRef: ElementRef;
  @ViewChild(PriorityDropdownComponent) priorityDropdownSelect: PriorityDropdownComponent;

  priorities: StrtPriority[];
  @Input() strategyId: number;
  @Output() getPlanHasInitiatives = new EventEmitter<number>();

  private readonly MAX_ACTIVITIES_PER_INITIATIVE = 10;
  private prioritiesSubscription: Subscription;
  private isFiltered = false;

  initiativePriorities: StrtPriority[];
  filteredInitiativePriorities: StrtPriority[];
  selectedOptions: PriorityDropdownOption[] = [];
  isLoading$: Observable<boolean>;
  planHasInitiatives = false;
  planHasPriorities = false;
  availableInitiatives: SelectItem[] = [];
  dropdownPriorityInitiatives: PriorityDropdownOption[] = [];
  initialLoad = true;
  getPrioritiesCalled = true;

  constructor(private appLoading: AppLoadingService,
              private _priorityService: PriorityService,
              private _strtDataStore: StrategiesDataStoreService,
              private _activityService: ActivityService,
              private cdr: ChangeDetectorRef,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepBy(PlanStep.DESIGN_THE_IMPLEMENTATION_PLAN), _resourceService);
    this.isLoading$ = this.appLoading.isLoading$;
  }

  ngOnInit() {
    console.log(`DIP -> Init() START`);
    this.prioritiesSubscription = this._strtDataStore.datedInitiativesWithActivities$.subscribe((priorities: StrtPriority[]) => {
      console.log(`DIP -> Priorities Handler Top`);
      this.priorities = priorities;
      this.planHasPriorities = this.priorities && this.priorities.length > 0;
      this.initiativePriorities = this.filterPrioritiesWithInitiatives(this.priorities);
      this.filteredInitiativePriorities = this.initiativePriorities;
      if (this.initiativePriorities && this.initiativePriorities.length > 0) {
        this.availableInitiatives = DataUtils.reshapePriorities(this.initiativePriorities);
        this.dropdownPriorityInitiatives = PriorityDropdownOption.fromPriorityInitiatives(this.initiativePriorities);
        this.planHasInitiatives = true;
      } else {
        this.availableInitiatives = [];
        this.dropdownPriorityInitiatives = [];
        this.planHasInitiatives = false;
      }
      if (this.isFiltered) {
        this.displaySelectedInitiatives();
      } else if (this.initialLoad) {
        setTimeout(() => {
          this.priorityDropdownSelect.selectAll();
          this.initialLoad = false;
        }, 100);
      }
      this.cdr.detectChanges();
    });
    this._priorityService.getPlanInitiativesWithActivities(this.strategyId);
  }

  ngOnDestroy(): void {
    if (this.prioritiesSubscription) {
      this.prioritiesSubscription.unsubscribe();
    }
  }

  get isActivityActive() {
    return this.activityComp.isActive;
  }

  set isActivityActive(isActive) {
    this.activityComp.isActive = isActive;
  }

  filterPrioritiesWithInitiatives(prioritiesList: StrtPriority[]): StrtPriority[] {
    return prioritiesList.filter(
      priority => {
        const objectivesWithInitiatives = priority.objectives.filter((objective) => {
          return objective.initiatives.length > 0;
        });
        return objectivesWithInitiatives.length > 0;
      }
    );
  }

  get isInitiativePrioritiesEmpty() {
    return this.getPrioritiesCalled && this.initiativePriorities.length < 1;
  }

  getInitiativeBorderColor(colorCode: string, index: number) {
    if (index === 0) {
      return colorCode;
    } else {
      return '';
    }
  }

  isLastActivity(activities: StrtActivity[], index: number) {
    return (activities.length - 1) === index;
  }

  addTargetsTitleBorder(activity: StrtActivity) {
    return activity.activityTargets.length === 0 && (activity.measure || '').length !== 0;
  }

  addMeasuresTitleBorder(activity: StrtActivity) {
    return activity.activityTargets.length !== 0 && (activity.measure || '').length === 0;
  }

  onClickAddActivity(initiative: StrtInitiative) {
    this.activityComp.createActivity(initiative);
    this.activitySidebarElementRef['containerViewChild'].nativeElement.scrollTop = 0;
  }

  onClickEditActivity(activity: StrtActivity, initiative: StrtInitiative) {
    this.activityComp.editActivity(activity, initiative);
    this.activitySidebarElementRef['containerViewChild'].nativeElement.scrollTop = 0;
  }

  onClickDeleteActivity(activity: StrtActivity) {
    this.onActivityDatesDelete(this.toActivityDates(activity));
  }

  onActivityCreate(activity: StrtActivity) {
    this.isActivityActive = false;
    this.onActivityDatesCreate(this.toActivityDates(activity));
  }

  onActivityEdit(activity: StrtActivity) {
    this.isActivityActive = false;
    this.onActivityDatesEdit(this.toActivityDates(activity));
  }

  isAddActivityDisabled(initiative: StrtInitiative) {
    return initiative.activities.length >= this.MAX_ACTIVITIES_PER_INITIATIVE;
  }

  private toActivityDates(activity: StrtActivity): StrtActivityDates {
    return {activity: activity, startDate: null, endDate: null};
  }

  displaySelectedInitiatives() {
    this.isFiltered = true;
    // fetch latest selections from dropdown component.
    const selectedItems: IPriorityDropdownOptionValue[] = this.priorityDropdownSelect.selectedOptions;

    if (selectedItems.length === 1 && selectedItems[0].primaryId === -1) {
      this.filteredInitiativePriorities = this.initiativePriorities;
    } else {
      this.filteredInitiativePriorities = [];
      const priorityIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.parentIds.priorityId;
      });
      const objectiveIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.parentIds.objectiveId;
      });
      const initiativeIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.primaryId;
      });

      let tempPriorities = JSON.parse(JSON.stringify(this.initiativePriorities));
      tempPriorities = tempPriorities.filter((priority: StrtPriority) => {
        if (priorityIds.indexOf(priority.priorityId) >= 0) {
          priority.objectives = priority.objectives.filter((objective: StrtObjective) => {
            if (objectiveIds.indexOf(objective.objectiveId) >= 0) {
              objective.initiatives = objective.initiatives.filter((initiative: StrtInitiative) => {
                return initiativeIds.indexOf(initiative.initiativeId) >= 0;
              });
              return true;
            }
            return false;
          });
          return true;
        }
        return false;
      });
      this.filteredInitiativePriorities = tempPriorities;
      this.cdr.detectChanges();
    }
  }

  fetchData() {
    this.isFiltered = false;
  }

  dropdownValueChanged(event) {
    this.selectedOptions = event;
  }

  /**********************************************************************
   * Activity Handlers
   **********************************************************************/

  onActivityDatesCreate(activityDates: StrtActivityDates) {
    activityDates.activity.strategyId = this.strategyId;
    this._activityService.createActivity(activityDates.activity)
      .subscribe((response) => {
        activityDates.activity.activityId = response.activityId;
        this._strtDataStore.updateActivity(activityDates.activity, this.priorities);
      }, (err) => {
        console.log(`Error creating activity dates: `, err);
      });
  }

  onActivityDatesEdit(activityDates: StrtActivityDates) {
    this._activityService.updateActivity(activityDates.activity)
      .subscribe(() => {
        this._strtDataStore.updateActivity(activityDates.activity, this.priorities);
      }, (err) => {
        console.error(`Error editing activity dates: `, err);
      });
  }

  onActivityDatesDelete(activityDates: StrtActivityDates) {
    this._activityService.deleteActivity(activityDates.activity)
      .subscribe(() => {
        this._strtDataStore.updateActivity(Object.assign(activityDates.activity, {initiativeId: undefined}), this.priorities);
      }, (err) => {
        console.error(`Error deleting activity dates`);
      });
  }
}
